// pages/LeaveNew/leave-balance.js
import React, { useState, useEffect } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import { FaCalendarCheck, FaCalendarTimes, FaCalendarAlt, FaHistory, FaSmile } from 'react-icons/fa';
import { BsCalendar2Date } from 'react-icons/bs';
import axios from 'axios';
import './leave-balance.css';
import LeaveTabs from '../../components/LeaveTabs';

const LeaveBalance = () => {
  const [loading, setLoading] = useState(true);
  const [balanceData, setBalanceData] = useState(null);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [historyError, setHistoryError] = useState(null);
  const [historyPagination, setHistoryPagination] = useState({
    page: 1,
    limit: 10,
    totalEntries: 0,
    totalPages: 0
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return '#28a745';
      case 'pending': return '#ffc107';
      case 'rejected': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getTypeColor = (type) => {
    return type === 'yearly' ? '#0353a4' : '#17a2b8';
  };

  useEffect(() => {
    fetchLeaveBalance();
    fetchLeaveHistory(1);
  }, []);

  const fetchLeaveBalance = async () => {
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        setError('User not logged in. Please login again.');
        setLoading(false);
        return;
      }

      const url = `https://backend.ems.hiradentallab.com/api/leavesmanual/balance/${userId}`;
      const response = await axios.get(url, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        const data = response.data.data;
        const transformedData = {
          yearly: {
            total: data.yearly.total,
            used: data.yearly.used,
            remaining: data.yearly.remaining
          },
          december: {
            total: data.december.total,
            used: data.december.used,
            remaining: data.december.remaining
          },
          total: {
            total: data.total.total,
            used: data.total.used,
            remaining: data.total.remaining
          }
        };
        setBalanceData(transformedData);
        setError(null);
      } else {
        setError(response.data.message || 'Unable to load leave balance.');
        setBalanceData(null);
      }
    } catch (err) {
      // 404 means no record exists → we show friendly message
      if (err.response?.status === 404) {
        setBalanceData(null);
        setError(null);
      } else {
        let errorMsg = 'Unable to fetch leave balance. Please try again later.';
        if (err.response?.data?.message) {
          errorMsg = err.response.data.message;
        }
        setError(errorMsg);
        setBalanceData(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveHistory = async (page, search = '') => {
    try {
      setHistoryLoading(true);
      const userId = localStorage.getItem('userId');
      if (!userId) {
        setHistoryError('User not logged in');
        setHistoryLoading(false);
        return;
      }

      const response = await axios.get(
        `https://backend.ems.hiradentallab.com/api/leavesmanual/leaves/${userId}`,
        {
          params: { page, limit: historyPagination.limit, search },
          headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
        }
      );

      if (response.data.success) {
        const transformedHistory = response.data.leaves.map((leave) => ({
          id: leave._id,
          dateRange: `${formatDate(leave.startDate)} - ${formatDate(leave.endDate)}`,
          type: leave.allowanceType || 'yearly',
          days: leave.daysCount || (leave.includedDates?.length || 0),
          status: leave.status || 'pending',
          reason: leave.reason || 'No reason provided'
        }));

        setLeaveHistory(transformedHistory);

        const totalEntries = response.data.totalEntries || transformedHistory.length;
        const totalPages = Math.ceil(totalEntries / historyPagination.limit);

        setHistoryPagination(prev => ({
          ...prev,
          page,
          totalEntries,
          totalPages
        }));

        setHistoryError(null);
      } else {
        setHistoryError(response.data.message || 'Failed to load leave history');
      }
    } catch (err) {
      setHistoryError('Unable to fetch leave history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= historyPagination.totalPages) {
      fetchLeaveHistory(newPage);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading your leave balance...</p>
      </div>
    );
  }

  // Friendly message when no leave allowance is set yet
  if (!balanceData && !error) {
    return (
      <>
        <LeaveTabs />
        <div className="no-allowance-container">
          <div className="no-allowance-content">
            <FaSmile size={80} color="#17a2b8" className="smile-icon" />
            <h3 className="no-allowance-title">Welcome!</h3>
            <p className="no-allowance-text">
              Your leave allowance hasn't been set up yet.<br />
              Once HR assigns your yearly and December holidays,<br />
              your balance will appear here and you'll be able to apply for leave.
            </p>
            <p className="no-allowance-subtext">
              Feel free to contact HR if you think this should already be ready.
            </p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <Alert variant="danger">{error}</Alert>
      </div>
    );
  }

  const data = balanceData;

  return (
    <>
      <LeaveTabs />
      <div className="leave-balance-container">
        <div className="page-header">
          <h2>
            <BsCalendar2Date className="header-icon" />
            Leave Balance Overview
          </h2>
          <p>Track your available leaves and usage history</p>
        </div>

        <div className="balance-cards-grid">
          <div className="balance-card yearly">
            <div className="card-icon-wrapper"><FaCalendarAlt /></div>
            <h4>Yearly Allowance</h4>
            <div className="balance-stats">
              <div className="stat-item"><span className="stat-label">Total</span><span className="stat-value total">{data.yearly.total} days</span></div>
              <div className="stat-item"><span className="stat-label">Used</span><span className="stat-value used">{data.yearly.used} days</span></div>
              <div className="stat-item"><span className="stat-label">Remaining</span><span className="stat-value remaining">{data.yearly.remaining} days</span></div>
            </div>
            <div className="progress-container">
              <div className="progress-label"><span>Usage</span><span>{data.yearly.total > 0 ? `${Math.round((data.yearly.used / data.yearly.total) * 100)}%` : '0%'}</span></div>
              <div className="progress-bar">
                <div className="progress-fill yearly-fill" style={{ width: data.yearly.total > 0 ? `${Math.min((data.yearly.used / data.yearly.total) * 100, 100)}%` : '0%' }}></div>
              </div>
            </div>
          </div>

          <div className="balance-card december">
            <div className="card-icon-wrapper"><FaCalendarTimes /></div>
            <h4>December Allowance</h4>
            <div className="balance-stats">
              <div className="stat-item"><span className="stat-label">Total</span><span className="stat-value total">{data.december.total} days</span></div>
              <div className="stat-item"><span className="stat-label">Used</span><span className="stat-value used">{data.december.used} days</span></div>
              <div className="stat-item"><span className="stat-label">Remaining</span><span className="stat-value remaining">{data.december.remaining} days</span></div>
            </div>
            <div className="progress-container">
              <div className="progress-label"><span>Usage</span><span>{data.december.total > 0 ? `${Math.round((data.december.used / data.december.total) * 100)}%` : '0%'}</span></div>
              <div className="progress-bar">
                <div className="progress-fill december-fill" style={{ width: data.december.total > 0 ? `${Math.min((data.december.used / data.december.total) * 100, 100)}%` : '0%' }}></div>
              </div>
            </div>
          </div>

          <div className="balance-card total">
            <div className="card-icon-wrapper"><FaCalendarCheck /></div>
            <h4>Total Summary</h4>
            <div className="balance-stats">
              <div className="stat-item"><span className="stat-label">Total</span><span className="stat-value total">{data.total.total} days</span></div>
              <div className="stat-item"><span className="stat-label">Used</span><span className="stat-value used">{data.total.used} days</span></div>
              <div className="stat-item"><span className="stat-label">Remaining</span><span className="stat-value remaining">{data.total.remaining} days</span></div>
            </div>
            <div className="progress-container">
              <div className="progress-label"><span>Overall Usage</span><span>{data.total.total > 0 ? `${Math.round((data.total.used / data.total.total) * 100)}%` : '0%'}</span></div>
              <div className="progress-bar">
                <div className="progress-fill total-fill" style={{ width: data.total.total > 0 ? `${Math.min((data.total.used / data.total.total) * 100, 100)}%` : '0%' }}></div>
              </div>
            </div>
            <div className="status-indicator">
              <span className={`status-badge ${
                data.total.remaining > (data.total.total * 0.5) ? 'good' :
                data.total.remaining > (data.total.total * 0.2) ? 'moderate' : 'low'
              }`}>
                {data.total.remaining > (data.total.total * 0.5) ? 'Good Balance' :
                 data.total.remaining > (data.total.total * 0.2) ? 'Moderate Balance' : 'Low Balance'}
              </span>
            </div>
          </div>
        </div>

        <div className="history-section">
          <div className="section-header">
            <FaHistory className="section-icon" />
            <h3>Leave History</h3>
          </div>

          {historyError && <Alert variant="danger" className="history-alert">{historyError}</Alert>}

          {historyLoading ? (
            <div className="loading-container small">
              <Spinner animation="border" size="sm" />
              <span>Loading history...</span>
            </div>
          ) : leaveHistory.length > 0 ? (
            <>
              <div className="history-table-container">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Date Range</th>
                      <th>Type</th>
                      <th>Days</th>
                      <th>Status</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveHistory.map((item) => (
                      <tr key={item.id}>
                        <td className="date-cell">{item.dateRange}</td>
                        <td>
                          <span className="type-badge" style={{ backgroundColor: getTypeColor(item.type) }}>
                            {item.type === 'yearly' ? 'Yearly' : 'December'}
                          </span>
                        </td>
                        <td className="days-cell">
                          <span className="days-count">{item.days} day{item.days > 1 ? 's' : ''}</span>
                        </td>
                        <td>
                          <span className="status-badge" style={{ backgroundColor: getStatusColor(item.status) }}>
                            {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                          </span>
                        </td>
                        <td className="reason-cell" title={item.reason}>{item.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {historyPagination.totalPages > 1 && (
                <div className="pagination-controls">
                  <button onClick={() => handlePageChange(historyPagination.page - 1)} disabled={historyPagination.page === 1 || historyLoading} className="pagination-btn">
                    Previous
                  </button>
                  <span className="pagination-info">
                    Page {historyPagination.page} of {historyPagination.totalPages}
                    <span className="total-entries"> ({historyPagination.totalEntries} entries)</span>
                  </span>
                  <button onClick={() => handlePageChange(historyPagination.page + 1)} disabled={historyPagination.page === historyPagination.totalPages || historyLoading} className="pagination-btn">
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-history">
              <p className="no-data-message">No leave history found</p>
              <p className="no-data-subtext">Your leave applications will appear here once submitted</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LeaveBalance;